import { Plus, MoreHorizontal } from 'lucide-react';
import type { Account } from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import { useAccounts } from '@/hooks/account/useAccounts';
import { useAccountProposals } from '@/hooks/account/use-account-proposals';
import { useState } from 'react';
import { NewAccountDialog } from '@/components/accounts/new-account-dialog';
import { PendingChangesAlert } from '@/components/shared/pending-changes-alert';
import { ReviewAccountCreationDialog } from '@/components/accounts/review-account-creation-dialog';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableHead,
} from '@/components/ui/styled-table';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  PageAction,
  PageHeaderInfo,
  PageContent,
} from '@/components/layout/page-header';
import { AccountRow } from '@/components/account-row';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AccountMenuOptions } from '@/components/accounts/account-menu-options';
import { EditAccountNameDialog } from '@/components/accounts/edit-account-name-dialog';
import { AddAccountNetworksDialog } from '@/components/accounts/add-account-networks-dialog';
import {
  ReorderableList,
  SortableItem,
} from '@/components/shared/reorderable-list';
import { ReorderError } from '@/components/shared/reorder-error';
import { useOrganizationItemOrder } from '@/hooks/admin/organization/use-organization-item-order';

const getAccountId = (account: Account) => account.id;

export function AccountsPage() {
  const { organization } = useMyOrganization();
  const isAdmin = organization?.isAdmin ?? false;
  const { accounts, refresh: refreshAccounts } = useAccounts();
  const {
    orderedItems: orderedAccounts,
    orderedIds: orderedAccountIds,
    reorder: reorderAccounts,
    isReordering,
    reorderError,
  } = useOrganizationItemOrder({
    items: accounts,
    itemType: 'accounts',
    getItemId: getAccountId,
    enabled: isAdmin,
  });
  const [isProposalStateAvailable, setIsProposalStateAvailable] =
    useState(true);
  const { proposals, refresh } = useAccountProposals({
    onSuccess: (data) => {
      // Case: defined proposal data from a successful refresh restores proposal availability.
      if (data !== undefined) {
        setIsProposalStateAvailable(true);
      }
    },
  });
  // TODO: DEN-27 Pull USD values for every token in this Organization from the server, and pass that into AccountRow for USD calculations
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [selectedAccountForRename, setSelectedAccountForRename] =
    useState<Account | null>(null);
  const [selectedAccountForAddNetworks, setSelectedAccountForAddNetworks] =
    useState<Account | null>(null);
  const [isEditAccountNameDialogOpen, setIsEditAccountNameDialogOpen] =
    useState(false);
  const [isAddAccountNetworksDialogOpen, setIsAddAccountNetworksDialogOpen] =
    useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Find pending proposals (not approved or rejected)
  // APPROVED status means the proposal has been executed
  const pendingProposals =
    proposals?.filter((p) => p.proposal.status === 'PENDING') ?? [];

  const handleEditAccountName = (account: Account) => {
    setSelectedAccountForRename(account);
    setIsEditAccountNameDialogOpen(true);
  };

  const handleAddAccountNetworks = (account: Account) => {
    setSelectedAccountForAddNetworks(account);
    setIsAddAccountNetworksDialogOpen(true);
  };

  const handleEditAccountNameDialogOpenChange = (open: boolean) => {
    setIsEditAccountNameDialogOpen(open);
    if (!open) {
      setSelectedAccountForRename(null);
    }
  };

  const handleAddAccountNetworksDialogOpenChange = (open: boolean) => {
    setIsAddAccountNetworksDialogOpen(open);
    if (!open) {
      setSelectedAccountForAddNetworks(null);
    }
  };

  return (
    <div className="">
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Accounts</PageTitle>
          <PageDescription>
            View and manage your organization's accounts
          </PageDescription>
        </PageHeaderInfo>
        <PageAction>
          <div className="flex flex-col items-end gap-4">
            {isAdmin && pendingProposals.length > 0 && (
              <PendingChangesAlert
                pendingChangesCount={pendingProposals.length}
                onReviewChanges={() => {
                  setIsReviewDialogOpen(true);
                }}
              />
            )}
            {isAdmin ? (
              <Button onClick={() => setIsNewDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                New Account
              </Button>
            ) : null}
          </div>
        </PageAction>
      </PageHeader>

      <PageContent>
        <ReorderError message={reorderError} />
        <StyledTableWrapper>
          <ReorderableList
            ids={orderedAccountIds}
            disabled={!isAdmin || isReordering}
            onReorder={reorderAccounts}
          >
            <StyledTable>
              <StyledTableHeader>
                <tr>
                  {isAdmin ? (
                    <StyledTableHead className="w-10 px-1 first:pl-1" />
                  ) : null}
                  <StyledTableHead className={isAdmin ? 'pl-0' : undefined}>
                    Account
                  </StyledTableHead>
                  <StyledTableHead className="w-[160px] whitespace-nowrap">
                    Total Value
                  </StyledTableHead>
                  <StyledTableHead>Blockchain Networks</StyledTableHead>
                  {isAdmin ? <StyledTableHead></StyledTableHead> : null}
                </tr>
              </StyledTableHeader>
              <StyledTableBody>
                {orderedAccounts.map((account) => (
                  <SortableItem
                    key={account.id}
                    id={account.id}
                    label={account.name}
                    disabled={!isAdmin || isReordering}
                  >
                    {({ setNodeRef, style, dragHandle }) => {
                      const tableRow = (
                        <AccountRow
                          ref={setNodeRef}
                          style={style}
                          account={account}
                          leadingCell={isAdmin ? dragHandle : undefined}
                          actionCell={
                            isAdmin ? (
                              <DropdownMenu
                                open={openDropdownId === account.id}
                                onOpenChange={(open) =>
                                  setOpenDropdownId(open ? account.id : null)
                                }
                              >
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 data-[state=open]:bg-muted"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-48 z-[9999]"
                                >
                                  <AccountMenuOptions
                                    account={account}
                                    onAddNetworks={handleAddAccountNetworks}
                                    onEditName={handleEditAccountName}
                                    onActionComplete={() =>
                                      setOpenDropdownId(null)
                                    }
                                    MenuItem={DropdownMenuItem}
                                  />
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : undefined
                          }
                        />
                      );

                      return isAdmin ? (
                        <ContextMenu>
                          <ContextMenuTrigger asChild>
                            {tableRow}
                          </ContextMenuTrigger>
                          <ContextMenuContent className="w-48 z-[9999]">
                            <AccountMenuOptions
                              account={account}
                              onAddNetworks={handleAddAccountNetworks}
                              onEditName={handleEditAccountName}
                              onActionComplete={() => {}}
                              MenuItem={ContextMenuItem}
                            />
                          </ContextMenuContent>
                        </ContextMenu>
                      ) : (
                        tableRow
                      );
                    }}
                  </SortableItem>
                ))}
              </StyledTableBody>
            </StyledTable>
          </ReorderableList>
        </StyledTableWrapper>
      </PageContent>

      {isAdmin ? (
        <NewAccountDialog
          open={isNewDialogOpen}
          onOpenChange={setIsNewDialogOpen}
          onProposalCreated={(_) => {
            refresh();
          }}
        />
      ) : null}

      {isAdmin ? (
        <ReviewAccountCreationDialog
          open={isReviewDialogOpen}
          onOpenChange={setIsReviewDialogOpen}
          proposals={pendingProposals}
          onApplied={() => {
            refresh();
          }}
          onAccountsChanged={() => {
            refreshAccounts();
          }}
          isProposalStateAvailable={isProposalStateAvailable}
          onProposalStateUnavailable={() => setIsProposalStateAvailable(false)}
          refreshProposals={refresh}
        />
      ) : null}

      {isAdmin ? (
        <AddAccountNetworksDialog
          open={isAddAccountNetworksDialogOpen}
          account={selectedAccountForAddNetworks}
          onOpenChange={handleAddAccountNetworksDialogOpenChange}
          onProposalCreated={() => {
            refresh();
          }}
        />
      ) : null}

      {isAdmin ? (
        <EditAccountNameDialog
          open={isEditAccountNameDialogOpen}
          account={selectedAccountForRename}
          onOpenChange={handleEditAccountNameDialogOpenChange}
          onUpdated={() => {
            refreshAccounts();
          }}
        />
      ) : null}
    </div>
  );
}
